import SearchField from "@entrilia/shared/components/inputs/SearchField";
import { Box, Stack } from "@mui/material";
import type { GridRowId, GridSortModel } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import { searchDimensionDictionary } from "../../../../../api/biApi";
import type { ConditionDescriptor } from "../../../../../api/types/fieldConfigurationTypes";
import useClientContext from "../../../../../context/useClientContext";
import SwitchField from "../../../reporting/sv/components/common/SwitchField";
import { getTextFilterGridColumns } from "../../../reporting/sv/components/viewer/common/conditions/grid/textFilterGridDataProvider";
import {
  textFilterGridSx,
  toConditionValue,
  toRowId,
} from "@entrilia/shared/components/reporting/text-filter-grid/filterGridHelper";
import TextFilterGrid from "@entrilia/shared/components/reporting/text-filter-grid/TextFilterGrid";
import {
  MAX_DICTIONARY_SEARCH_LENGTH,
  type TextFilterGridLoadParams,
  type TextFilterGridLoadResult,
} from "@entrilia/shared/components/reporting/text-filter-grid/textFilterGridTypes";
import { getTextFilterGrouping } from "../../../reporting/sv/components/viewer/common/conditions/text-filter-grid-with-lazyloading/textFilterGrouping";
import useCachedTextFilterGridLoadRows from "@entrilia/shared/components/reporting/text-filter-grid/useCachedTextFilterGridLoadRows";
import type { DashboardFilter } from "../../dashboard.types";
import { buildFilterValuesQuery, toFilterValue } from "./filterValuesQuery";

const SEARCH_MIN_ITEM_COUNT = 10;

interface Props {
  filter: DashboardFilter;
  selectedValues: unknown[];
  singleSelect?: boolean;
  hideSecondaryColumn?: boolean;
  allowShowOnlySelected?: boolean;
  parentCondition?: ConditionDescriptor;
  onSelectedValuesChange: (values: (string | null)[]) => void;
}

const FilterValuesGrid = ({
  filter,
  selectedValues,
  singleSelect,
  hideSecondaryColumn,
  allowShowOnlySelected,
  parentCondition,
  onSelectedValuesChange,
}: Props) => {
  const { clientCode } = useClientContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [unfilteredItemCount, setUnfilteredItemCount] = useState<number>();
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  const keyFieldName = filter.valueField || "";
  const { request, queryKey } = buildFilterValuesQuery({
    filter,
    searchTerm,
    parentCondition,
    onlySelectedValues: showOnlySelected ? selectedValues : undefined,
  });

  const grouping = useMemo(
    () =>
      getTextFilterGrouping({
        isMultiBook: filter.isMultiBook,
        dimensionName: filter.id,
        keyFieldName,
        books: filter.books,
      }),
    [filter.books, filter.id, filter.isMultiBook, keyFieldName]
  );
  const availableFields = useMemo(
    () =>
      hideSecondaryColumn === true && filter.availableFields.includes(keyFieldName)
        ? [keyFieldName]
        : filter.availableFields,
    [filter.availableFields, hideSecondaryColumn, keyFieldName]
  );
  const columns = useMemo(
    () => getTextFilterGridColumns({ availableFields, keyFieldName }),
    [availableFields, keyFieldName]
  );
  const initialSorting = useMemo((): GridSortModel => [{ field: keyFieldName, sort: "asc" }], [keyFieldName]);
  const selectedRowIds = useMemo(() => selectedValues.map((value) => toRowId(toFilterValue(value))), [selectedValues]);

  const loadDictionaryRows = useCallback(
    async (params: TextFilterGridLoadParams): Promise<TextFilterGridLoadResult> => {
      const { range, sortModel, allRows } = params;
      const [sortItem] = sortModel;
      const response = await searchDimensionDictionary(filter.dataSetId, {
        ...request,
        allRows,
        range,
        sort: sortItem ? { fieldId: sortItem.field, ascending: sortItem.sort !== "desc" } : undefined,
      });
      if (!response.success) {
        throw new Error(response.error?.message ?? "Failed to load filter values");
      }
      const isUnfilteredQuery =
        request.searchTermUserInput === undefined && request.selectedValuesUserInput === undefined;
      if (isUnfilteredQuery && response.data.total !== undefined) {
        setUnfilteredItemCount(response.data.total);
      }
      return { rows: response.data.items, rowCount: response.data.total };
    },
    [filter.dataSetId, request]
  );

  const cacheScope = useMemo(
    () => [clientCode, filter.dataSetId, filter.id],
    [clientCode, filter.dataSetId, filter.id]
  );
  const loadRows = useCachedTextFilterGridLoadRows({
    loadRows: loadDictionaryRows,
    queryKey,
    cacheScope,
    persistent:
      request.searchTermUserInput === undefined &&
      request.conditions === undefined &&
      request.selectedValuesUserInput === undefined,
  });

  const handleSelectionChanged = useCallback(
    (rowIds: GridRowId[]) => onSelectedValuesChange(rowIds.map(toConditionValue)),
    [onSelectedValuesChange]
  );

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      {(unfilteredItemCount === undefined || unfilteredItemCount > SEARCH_MIN_ITEM_COUNT) && (
        <SearchField onSearch={setSearchTerm} debounceTimeMs={500} maxLength={MAX_DICTIONARY_SEARCH_LENGTH} fullWidth />
      )}
      {allowShowOnlySelected === true && (
        <SwitchField
          label="Show only selected"
          checked={showOnlySelected}
          containerSx={{ width: "100%", flex: "0 0 auto" }}
          onChange={setShowOnlySelected}
        />
      )}
      <Box sx={{ flex: 1, display: "flex", overflow: "auto", maxWidth: "100%" }}>
        <TextFilterGrid
          initialSorting={initialSorting}
          columns={columns}
          loadRows={loadRows}
          queryKey={queryKey}
          selectedRowIds={selectedRowIds}
          singleSelect={singleSelect}
          containerMinHeight={0}
          grouping={grouping}
          sx={textFilterGridSx}
          onChanged={handleSelectionChanged}
        />
      </Box>
    </Stack>
  );
};

export default FilterValuesGrid;
